import React, { useState } from "react"
import { FaSpinner } from "react-icons/fa"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import Button from "@fuse/sharedComponents/buttons/SolidBtn"

interface NonTicketedEventReasonPopupProps {
  isLoading: boolean
  onClose: () => void
  onConfirmLeaveNonTicketedEvent: (v: string) => void
}

const leaveEventReason = [
  "I no longer want to attend",
  "I misread the details",
  "I overcommitted",
  "I found better plans",
  "I don't feel comfortable with this event",
  "Other",
]

const NonTicketedEventReasonPopup: React.FC<
  NonTicketedEventReasonPopupProps
> = ({ isLoading, onClose, onConfirmLeaveNonTicketedEvent }) => {
  const [selectedReason, setSelectedReason] = useState<string>("")
  const [otherReason, setOtherReason] = useState<string>("")

  const handleReasonChange = (value: string) => {
    setSelectedReason(value)
    if (value !== "Other") {
      setOtherReason("")
    }
  }

  const handleOtherReasonChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setOtherReason(e.target.value)
  }

  const handleSubmit = () => {
    if (selectedReason === "Other") {
      console.log("Other reason:", otherReason)
      onConfirmLeaveNonTicketedEvent(otherReason)
    } else {
      onConfirmLeaveNonTicketedEvent(selectedReason)
    }
  }

  const handleOverlayClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    onClose()
  }

  const handlePopupContentClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation()
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-[1000] w-screen h-screen"
      onClick={handleOverlayClick}
    >
      <BlurWrapper
        overlayOpacityDefault="20"
        className="rounded-2xl text-center max-w-sm max-h-[90vh] overflow-y-auto md:max-w-lg flex flex-col"
      >
        <div
          className="flex w-full h-full items-center justify-center"
          onClick={handlePopupContentClick}
        >
          <div className="px-[30px] py-[28px] w-full max-w-xl">
            <h2 className="text-2xl mb-[32px] text-white font-[400]">
              Please share why you're not attending the event
            </h2>
            <form>
              <div className="flex flex-col">
                {leaveEventReason.map((reason) => (
                  <div
                    key={reason}
                    className={`py-[12px] mb-[24px] border-[1px] border-white/30 flex justify-between items-center cursor-pointer rounded-[8px] ${
                      selectedReason === reason ? "bg-white/30" : ""
                    }`}
                    onClick={() => handleReasonChange(reason as string)}
                  >
                    <div className="flex-1 text-center text-white/80">
                      {reason}
                    </div>
                    <input
                      type="radio"
                      name="reason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={(e) => handleReasonChange(e.target.value)}
                      className="hidden"
                    />
                  </div>
                ))}
              </div>

              {selectedReason === "Other" && (
                <textarea
                  value={otherReason}
                  onChange={handleOtherReasonChange}
                  className="w-full my-4 p-3 border border-white/20 rounded-xl bg-white/10 text-white min-h-24"
                  placeholder="Write your reasons here."
                />
              )}

              <div className="flex flex-col justify-center mt-8">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  <p className="font-[500]">
                    {isLoading ? (
                      <FaSpinner className="animate-spin" size={22} />
                    ) : (
                      "Leave event"
                    )}
                  </p>
                </Button>
                <button
                  onClick={onClose}
                  className="bg-transparent text-white px-4 py-2 rounded-full mx-12 mt-2 border-transparent focus:outline-none focus:ring-0 border-none"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </BlurWrapper>
    </div>
  )
}

export default NonTicketedEventReasonPopup
